import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCpu, FiServer, FiLayout, FiZap, FiDatabase } from 'react-icons/fi';

const icons = {
    dev: FiCpu,
    homelab: FiServer,
    design: FiLayout,
    morning: FiZap,
    mcp: FiDatabase,
};

const AIWorkflow = () => {
    const { t } = useTranslation();
    const cards = ['dev', 'homelab', 'design', 'morning', 'mcp'];

    return (
        <section id="ai" className="relative section">
            {/* Background orb for visual continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div
                    animate={{ x: [0, 40, 0], y: [0, -60, 0] }}
                    transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/3 -right-20 w-80 h-80 bg-amber-200/20 dark:bg-amber-900/10 rounded-full blur-[100px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="mb-12">
                        <span className="section-number">06 — AI Workflow</span>
                        <h2 className="section-title mt-2">{t('aiWorkflow.sectionTitle')}</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {cards.map((cardKey, ci) => {
                            const Icon = icons[cardKey];
                            const steps = t(`aiWorkflow.cards.${cardKey}.steps`, { returnObjects: true });
                            // Odd card out spans the full row
                            const wide = cards.length % 2 === 1 && ci === cards.length - 1;

                            return (
                                <motion.div
                                    key={cardKey}
                                    className={`terminal-card overflow-hidden${wide ? ' md:col-span-2' : ''}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: ci * 0.1 }}
                                >
                                    <div className="terminal-header">
                                        <span className="terminal-dot bg-red-400/80" />
                                        <span className="terminal-dot bg-amber-400/80" />
                                        <span className="terminal-dot bg-green-400/80" />
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-3">
                                            {t(`aiWorkflow.cards.${cardKey}.label`)}
                                        </span>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                                                <Icon size={18} className="text-amber-500 dark:text-amber-400" />
                                            </div>
                                            <h3 className="font-bold text-zinc-900 dark:text-zinc-50">
                                                {t(`aiWorkflow.cards.${cardKey}.title`)}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                                            {t(`aiWorkflow.cards.${cardKey}.description`)}
                                        </p>

                                        <div className={wide ? 'grid md:grid-cols-2 gap-4' : 'space-y-4'}>
                                            {steps.map((step, si) => (
                                                <div key={si} className="space-y-1.5 p-3 rounded-xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200/50 dark:border-zinc-700/50">
                                                    <code className="block font-mono text-[11px] text-amber-600 dark:text-amber-400 font-bold">
                                                        {step.cmd}
                                                    </code>
                                                    <p className="text-[11px] text-zinc-500 dark:text-zinc-500 leading-snug font-medium">
                                                        {step.desc}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    <p className="mt-8 text-center font-mono text-xs text-zinc-400 dark:text-zinc-500">
                        {t('aiWorkflow.poweredBy')}
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default AIWorkflow;
